// Determines what operators can be used for a given property type

import * as Operators from 'customer-data-filters-ui/filterQueryFormat/operator/Operators';
import { BOOLEAN, DATE, DATE_TIME, ENUMERATION, NUMBER, STRING } from 'customer-data-objects/property/PropertyTypes';
import { OrderedSet } from 'immutable';
import { INBOUND_DB_LIST } from 'customer-data-objects/property/ExternalOptionTypes';
const listMembershipOperators = OrderedSet.of(Operators.In, Operators.NotIn);
const getSnowflakeIdOperators = () => {
  return OrderedSet([Operators.In, Operators.NotIn, Operators.Equal, Operators.NotEqual, Operators.Known, Operators.NotKnown]);
};
const PHONE_NUMBER = 'phone_number';
const getCommonSnowflakeOperators = type => {
  switch (type) {
    case PHONE_NUMBER:
      return OrderedSet.of(Operators.Known, Operators.NotKnown);
    case BOOLEAN:
      return OrderedSet.of(Operators.Equal, Operators.NotEqual, Operators.Known, Operators.NotKnown, Operators.EverEqual, Operators.NeverEqual, Operators.UpdatedInLastXDays, Operators.NotUpdatedInLastXDays);
    case DATE:
    case DATE_TIME:
      return OrderedSet.of(Operators.InRollingDateRange, Operators.Equal, Operators.BeforeDate, Operators.AfterDate, Operators.InRange, Operators.NotInRange, Operators.GreaterRolling, Operators.LessRollingWithOrWithoutYear, Operators.Known, Operators.NotKnown, Operators.UpdatedInLastXDays, Operators.NotUpdatedInLastXDays);
    case ENUMERATION:
      return OrderedSet.of(Operators.In, Operators.NotIn, Operators.Known, Operators.NotKnown, Operators.EverIn, Operators.NeverIn, Operators.EqualAll, Operators.NotEqualAll, Operators.ContainAll, Operators.NotContainAll, Operators.EverEqualAll, Operators.NeverEqualAll, Operators.EverContainedAll, Operators.NeverContainedAll, Operators.UpdatedInLastXDays, Operators.NotUpdatedInLastXDays);
    case NUMBER:
      return OrderedSet.of(Operators.Equal, Operators.NotEqual, Operators.Less, Operators.LessOrEqual, Operators.Greater, Operators.GreaterOrEqual, Operators.Known, Operators.NotKnown, Operators.EverEqual, Operators.NeverEqual, Operators.UpdatedInLastXDays, Operators.NotUpdatedInLastXDays);
    case STRING:
      return OrderedSet.of(Operators.EqualAny, Operators.NotEqualAny, Operators.ContainAny, Operators.NotContainAny, Operators.StartsWithAny, Operators.EndsWithAny, Operators.Known, Operators.NotKnown, Operators.EverEqualAny, Operators.NeverEqualAny, Operators.EverContained, Operators.NeverContained, Operators.UpdatedInLastXDays, Operators.NotUpdatedInLastXDays);
    case INBOUND_DB_LIST:
      return listMembershipOperators;
    default:
      return undefined;
  }
};
const getSnowflakeObjectOperators = type => {
  return getCommonSnowflakeOperators(type);
};
const HIDDEN_EVENT_OPERATORS = [Operators.EverContained, Operators.EverContainedAll, Operators.EverContainedAny, Operators.EverEqual, Operators.EverEqualAny, Operators.NeverContained, Operators.NeverContainedAll, Operators.NeverEqual, Operators.NeverEqualAny, Operators.NotUpdatedInLastXDays, Operators.UpdatedInLastXDays];
const getSnowflakeEventOperators = type => {
  const commonOperators = getCommonSnowflakeOperators(type);
  if (!commonOperators) {
    return undefined;
  }
  return HIDDEN_EVENT_OPERATORS.reduce((visibleOperators, hiddenOperator) => visibleOperators.remove(hiddenOperator), commonOperators);
};
const HIDDEN_EXPRESSION_OPERATORS = [Operators.EverContained, Operators.EverEqual, Operators.EverEqualAny, Operators.NeverContained, Operators.NeverEqual, Operators.NeverEqualAny, Operators.NotUpdatedInLastXDays, Operators.UpdatedAfter, Operators.UpdatedBefore, Operators.UpdatedInLastXDays];
const getSnowflakeExpressionOperators = type => {
  const commonOperators = getCommonSnowflakeOperators(type);
  if (!commonOperators) {
    return undefined;
  }
  return HIDDEN_EXPRESSION_OPERATORS.reduce((visibleOperators, hiddenOperator) => visibleOperators.remove(hiddenOperator), commonOperators);
};
const getUnifiedAnalyticsOperators = type => {
  switch (type) {
    case BOOLEAN:
      return OrderedSet.of(Operators.Equal, Operators.NotEqual);
    case DATE:
    case DATE_TIME:
      return OrderedSet.of(Operators.Less, Operators.Greater, Operators.InRange);
    case ENUMERATION:
      return OrderedSet.of(Operators.Equal, Operators.In);
    case NUMBER:
      return OrderedSet.of(Operators.Equal, Operators.GreaterOrEqual, Operators.LessOrEqual, Operators.InRange);
    case STRING:
      return OrderedSet.of(Operators.Equal, Operators.In);
    default:
      return undefined;
  }
};
const getDashboardOperators = type => {
  switch (type) {
    case BOOLEAN:
      return OrderedSet.of(Operators.Equal, Operators.NotEqual);
    case DATE:
    case DATE_TIME:
      return OrderedSet.of(Operators.InRollingDateRange, Operators.Equal, Operators.AfterDate, Operators.BeforeDate, Operators.InRange, Operators.Known, Operators.NotKnown);
    case ENUMERATION:
      return OrderedSet.of(Operators.In, Operators.NotIn, Operators.Known, Operators.NotKnown);
    case NUMBER:
      return OrderedSet.of(Operators.Equal, Operators.NotEqual, Operators.Greater, Operators.GreaterOrEqual, Operators.Less, Operators.LessOrEqual, Operators.Known, Operators.NotKnown);
    case STRING:
      return OrderedSet.of(Operators.ContainAny, Operators.NotContainAny, Operators.Known, Operators.NotKnown);
    case INBOUND_DB_LIST:
      return listMembershipOperators;
    default:
      return undefined;
  }
};
const getDynamicDateOperators = type => {
  switch (type) {
    case DATE:
    case DATE_TIME:
      return OrderedSet.of(Operators.Equal, Operators.AfterDate, Operators.BeforeDate, Operators.InRange);
    default:
      return undefined;
  }
};
export const getReportingOperatorsForType = reportingFilterContext => {
  switch (reportingFilterContext) {
    case 'dashboard':
      return getDashboardOperators;
    case 'unified_analytics':
      return getUnifiedAnalyticsOperators;
    case 'analytics_view':
      return () => OrderedSet.of(Operators.Equal);
    case 'dynamic_date':
      return getDynamicDateOperators;
    case 'snowflake_id':
      return getSnowflakeIdOperators;
    case 'snowflake_object':
      return getSnowflakeObjectOperators;
    case 'snowflake_event':
      return getSnowflakeEventOperators;
    case 'snowflake_expression':
      return getSnowflakeExpressionOperators;
    default:
      throw new Error(`Unsupported reporting filter context: ${reportingFilterContext}`);
  }
};